import React, { useState, useEffect, useRef, RefObject, useMemo } from 'react'
import dynamic from 'next/dynamic'
import type { GetStaticPropsContext } from 'next'
import { Grid, styledComponent, SxProps } from '@hermes/web-components'
import { mergeStatisticByName } from '../../../utils/statistic'
import getLanguageFromLocale from '../../../utils/getLanguageFromLocale'
import { ProfileContextProvider } from '../../../providers/AppData/Profile'
import { Keywords, Percentage, ProfileKeyword, Statistic, ProfileReview } from '../../../types'
import { getBreadcrumbByLocationId } from '../../../api/specialist'
import Breadcrumbs from '../../../components/Breadcrumbs'
import SectionWrapper from '../../../components/profiles/SectionWrapper'
import withError from '../../../components/withError'
import PageMeta from '../../../components/ProfileMeta'
import { SearchReviewsProvider } from '../../../providers/SearchReviews'
import { SearchSpecialistsProvider } from '../../../providers/SearchSpecialists'
import usePracticeProfileMeta from '../../../hooks/usePracticeProfileMeta'
import {
  getPracticeBySlug,
  getPracticeReviews,
  getPracticeLatestReview,
  getPracticeSpecialists
} from '../../../api/practice'
import { checkProfileSlugRedirect } from '../../../utils/checkProfileSlugRedirect'
import { PracticePlans, limitReviews, limitSpecialists } from '../../../constants/profile'
import { filteringProfileDataByPlan } from '../../../utils/filteringDataByPlan'
import { PracticeProfile, PracticeSpecialist } from '../../../types/practice'
import MediaCenter from '../../../components/profiles/Media'
import ProfileIntro from '../../../components/profiles/Intro'
import ProfileAbout from '../../../components/profiles/About'
import ProfileTabs from '../../../components/profiles/ProfileTabs'
import PracticeLocations from '../../../components/profiles/practice/PracticeLocations'
import PracticeSpecialists from '../../../components/profiles/practice/PracticeSpecialists'
import Reviews from '../../../components/profiles/Reviews'
import RatingBar from '../../../components/RatingBar'
import SpecialistTrustBanner from '../../../components/profiles/specialist/TrustBanner'
import Gallery from '../../../components/profiles/Gallery'
import FAQ from '../../../components/profiles/practice/FAQ'
import ServicesSection from '../../../components/profiles/Services'
import ParkingSection from '../../../components/profiles/Parking'
import { usePracticePageConfig } from '../../../hooks/usePracticePageConfig'
import { themedColor } from '../../../components/profiles/styles'
import { useWpaTenant } from '../../../hooks/useWpaTenant'
import returnNextErrorPage from '../../../utils/returnNextErrorPage'
import { breadcrumbsObjectToArray } from '../../../utils/breadcrumbsObjectToArray'
import { ProfileTypes, SearchTypes } from '../../../providers/SearchProfilesPage/types'
import { Breadcrumb } from '../../../types/breadcrumbs'
import { TENANTS, newSearchTypesPractices } from '../../../constants/global'
import { getFeatureFlags } from '../../../hooks/useFeatureFlags'
import { transformSearchType } from '../../../utils/transformAutocomplete'
import { withStaticPropsMocks } from '../../../playwright/utils/withMockingProps'
import { useKeywordsMapper } from '../../../hooks/useKeywordsMapper'
import { useWorkingHoursToStringGrouped } from '../../../utils/workingHours'
import useScrollEndEffect from '../../../hooks/useScrollEndEffect'
import { BasicProfileAlert } from '../../../components/profiles/BasicProfileAlert'
import JsonLd from '../../../components/JsonLd'

const VideoPlayerModal = dynamic(() => import('../../../components/profiles/VideoPlayerModal'), { ssr: false })
const FacilitiesSection = dynamic(() => import('../../../components/profiles/Facilities'), { ssr: false })

const BookAppointmentModal = dynamic(() => import('../../../components/profiles/ProfileModal/BookAppointmentModal'), {
  ssr: false
})

export const ReviewBarWrapper = styledComponent(Grid)(() => ({
  position: 'relative'
}))

type PracticeMainPageProps = {
  practice: PracticeProfile
  errorCode?: number
  latestReviews: ProfileReview[]
  latestReview: ProfileReview
  isProOrExpertPro: boolean
  isEntry: boolean
  practiceSpecialists: PracticeSpecialist[]
  totalReviews: number
  isUnclaimedEntry: boolean
  isGetInTouchDisabledPlan: boolean
  breadcrumbs: { label: string; url: string; id: number }[]
  totalSpecialists: number
  topKeyword: ProfileKeyword
  percentage: Percentage
  keywordNames: Keywords[]
}

const IntroSectionWrapper = styledComponent(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(13),
  [theme.breakpoints.down('lg')]: {
    paddingTop: theme.spacing(18)
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(18)
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(8)
  }
}))

const whiteColor = 'common.white'

const facilitiesSectionSpacing = { lg: '48px', xs: '40px' }

// eslint-disable-next-line complexity
function PracticeMainPage({
  practice,
  latestReviews,
  latestReview,
  practiceSpecialists,
  totalReviews,
  isUnclaimedEntry,
  isGetInTouchDisabledPlan,
  isProOrExpertPro,
  isEntry,
  topKeyword,
  totalSpecialists,
  breadcrumbs,
  percentage,
  keywordNames
}: PracticeMainPageProps) {
  const pageMeta = usePracticeProfileMeta({ profile: practice })
  const {
    router,
    theme,
    language,
    profileAnalytics,
    introVideoAvailable,
    hideBookAppointment,
    contacts,
    pageSchema,
    visibilityComponents,
    publications,
    pinned,
    hideGetInTouch,
    tenantSettings,
    reviewStatistic,
    showFacilities,
    services,
    parking,
    facilities,
    mappedBreadcrumbs,
    profileVideo
  } = usePracticePageConfig({
    practice,
    breadcrumbs,
    isUnclaimedEntry,
    topKeyword,
    isGetInTouchDisabledPlan
  })
  const { practiceLocationSectionBackground, buttonBackground, endorsementsBackground } = themedColor()
  const { isWPA } = useWpaTenant()

  const [showContactForm, setShowContactForm] = useState(false)
  const [videoPlayerVisibility, setVideoPlayerVisibility] = useState(false)
  const [ratingBarEndAnchorRef, setRatingBarEndAnchorRef] = useState<RefObject<HTMLDivElement> | undefined>(undefined)

  const ratingBarScrollEndFacilities = useRef<HTMLDivElement>(null)
  const ratingBarScrollEndMediaCenter = useRef<HTMLDivElement>(null)

  const isImproveListingVisible = !isWPA && (isUnclaimedEntry || isEntry)

  const toggleContactForm = () => setShowContactForm((prev: boolean) => !prev)

  const handleRouteChange = (url: string) => {
    if (url === `/${router.locale}${router.asPath}`) {
      window.dataLayer.push({
        event: 'prf_spa_page_change_ready',
        ...profileAnalytics
      })
    }
  }

  useScrollEndEffect(() => {
    const routerHash = router.asPath.split('#')[1]
    const windowHash = window.location.hash.split('#')[1]
    const hash = routerHash || windowHash

    if (hash) {
      const selectedComponent = document.getElementById(hash)

      if (selectedComponent) {
        selectedComponent.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [router.asPath])

  useEffect(() => {
    const refs = [ratingBarScrollEndFacilities, ratingBarScrollEndMediaCenter]

    for (let i = 0; i < refs.length; i++) {
      const ref = refs[i]
      if (ref.current) {
        setRatingBarEndAnchorRef(ref)
        break
      }
    }
  }, [ratingBarScrollEndFacilities, ratingBarScrollEndMediaCenter])

  useEffect(() => {
    if (window?.dataLayer) {
      window.dataLayer.push(profileAnalytics)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  const workingHoursString = useWorkingHoursToStringGrouped(practice.workingOpeningHours)

  const { specialties } = useKeywordsMapper(practice.keywords, true)

  const specialtiesToShow = useMemo(
    () => specialties.slice(0, 2).map((specialty) => specialty.name?.[language]),
    [language, specialties]
  )

  const ProfileIntroElement = ({ sx = {} }: { sx?: SxProps }) => (
    <Grid item sx={sx || {}} xs={12} lg={7}>
      <ProfileIntro
        id={practice.externalId}
        slug={practice.slug}
        name={practice.name[language]}
        image={practice.images?.cover}
        address={practice.address}
        plan={practice.plan}
        isPractice
        premium={practice?.customFields?.premium}
        isBasic={practice.basic}
        isUnclaimed={isUnclaimedEntry}
        introVideoAvailable={introVideoAvailable}
        setVideoPlayerVisibility={setVideoPlayerVisibility}
        profileAnalytics={profileAnalytics}
        connectionsCount={practice.followersCount}
        review={practice.reviewObject}
        workingTimeString={workingHoursString}
        specialtiesList={specialtiesToShow}
        specialtiesCount={specialties.length}
        specialistsCount={totalSpecialists}
        followersCount={practice.followersCount}
      />
    </Grid>
  )

  const ratingBarHasContent = useMemo(
    () => !(practice.basic || isUnclaimedEntry) || isWPA,
    [isUnclaimedEntry, practice.basic, isWPA]
  )

  return (
    <>
      <PageMeta {...pageMeta}>
        <JsonLd schema={pageSchema} />
      </PageMeta>
      <ProfileContextProvider practice={practice}>
        <SearchReviewsProvider
          language={language}
          keywordNames={keywordNames}
          reviews={latestReviews}
          profileId={practice.id}
          currentLocation={breadcrumbs.slice(-1).pop()}
          totalCount={totalReviews}
          isPractice
        >
          <ProfileTabs smUp implementation="css" practice={practice} isUnclaimed={isUnclaimedEntry} />
          <SectionWrapper
            sx={{
              backgroundColor: {
                xs: theme.custom?.SpecialistPage?.common?.background ?? whiteColor,
                lg: theme.custom?.SpecialistPage?.common?.background ?? 'background.default'
              }
            }}
            id="overview"
          >
            <IntroSectionWrapper container>
              <ProfileIntroElement />
              <ReviewBarWrapper sx={{ mb: ratingBarHasContent ? 0 : '-16px' }} item xs={12} lg={5}>
                <RatingBar
                  profileName={practice.name[language]}
                  review={practice.reviewObject}
                  reviewsTotal={totalReviews}
                  averageRating={practice.averageRating}
                  latestReview={latestReview}
                  slug={practice.slug}
                  externalIdProfile={practice.externalId}
                  type="clinic"
                  statistic={practice.statistic}
                  onBookAppointment={toggleContactForm}
                  isUnclaimed={isUnclaimedEntry}
                  hideBookAppointment={hideBookAppointment}
                  isBasic={practice.basic}
                  omitContainerSpacing={!ratingBarHasContent}
                  endAnchorRef={ratingBarEndAnchorRef}
                  contacts={contacts}
                />
              </ReviewBarWrapper>
            </IntroSectionWrapper>
          </SectionWrapper>
          <ProfileTabs smDown implementation="css" practice={practice} isUnclaimed={isUnclaimedEntry} />
          {visibilityComponents.about && (
            <SectionWrapper withPadding sx={{ backgroundColor: whiteColor }}>
              <Grid container>
                {isUnclaimedEntry && router.locale !== TENANTS.WPA && (
                  <Grid item lg={7} md={12} xs={12} sx={{ mb: 2 }}>
                    <BasicProfileAlert
                      id={practice.externalId}
                      slug={practice.slug}
                      plan={practice.plan}
                      name={practice.name[language]}
                      isPractice
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12} lg={7}>
                  <ProfileAbout
                    isBasic={practice.basic}
                    about={practice.about?.[language]}
                    keywords={practice.keywords}
                    insurers={practice.insurers}
                    statistic={practice.statistic}
                    isUnclaimed={isUnclaimedEntry}
                    name={practice.name[language]}
                    healthChecks={practice.healthChecks}
                    publications={publications}
                    pinnedFacilities={pinned}
                    isImproveListingVisible={isImproveListingVisible}
                    isClinic
                  />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          {visibilityComponents.location && (
            <SectionWrapper
              withPadding
              sx={{
                background: practiceLocationSectionBackground,
                backgroundBlendMode: 'screen, normal, normal',
                backgroundPosition: 'bottom',
                backgroundSize: 'cover'
              }}
              id="locations"
            >
              <Grid container>
                <Grid item xs={12} md={12} lg={7}>
                  <PracticeLocations
                    address={practice.address}
                    hideGetInTouch={hideGetInTouch}
                    workingHours={practice.workingOpeningHours}
                    defaultAddress={tenantSettings?.defaultSearch}
                    isAvailable={!!contacts?.length}
                  />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          {visibilityComponents.reviews && (
            <SectionWrapper withPadding id="reviews">
              <Grid container>
                <Grid item xs={12} md={12} lg={7}>
                  <Reviews
                    id={practice.externalId}
                    plan={practice.plan}
                    statistic={reviewStatistic}
                    slug={practice.slug}
                    isProOrExpertPro={isProOrExpertPro}
                    isEntry={isEntry}
                    isBasic={practice.basic}
                    name={practice.name[language]}
                    isUnclaimed={isUnclaimedEntry}
                    percentage={percentage}
                    isPractice
                  />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          <SectionWrapper sx={{ backgroundColor: buttonBackground }}>
            <SpecialistTrustBanner />
          </SectionWrapper>
          {visibilityComponents.specialists && (
            <SectionWrapper withPadding sx={{ backgroundColor: endorsementsBackground }} id="specialists">
              <SearchSpecialistsProvider specialists={practiceSpecialists} totalCount={totalSpecialists}>
                <Grid container>
                  <Grid item lg={7} xs={12}>
                    <PracticeSpecialists slug={practice.slug} />
                  </Grid>
                  <Grid item xs={12} lg={5} />
                </Grid>
              </SearchSpecialistsProvider>
            </SectionWrapper>
          )}
          {showFacilities && (
            <section ref={ratingBarScrollEndFacilities} id="services-facilities">
              {visibilityComponents.services && (
                <SectionWrapper
                  sx={{
                    backgroundColor: whiteColor,
                    pt: facilitiesSectionSpacing,
                    pb: visibilityComponents.parking ? 0 : facilitiesSectionSpacing
                  }}
                  id="services"
                >
                  <ServicesSection services={services} />
                </SectionWrapper>
              )}
              {visibilityComponents.parking && (
                <SectionWrapper sx={{ backgroundColor: whiteColor, py: facilitiesSectionSpacing }} id="parking">
                  <ParkingSection parking={parking} />
                </SectionWrapper>
              )}
              {visibilityComponents.facilities && (
                <SectionWrapper
                  sx={{ backgroundColor: 'background.default', py: facilitiesSectionSpacing }}
                  id="facilities"
                >
                  <FacilitiesSection facilities={facilities} />
                </SectionWrapper>
              )}
            </section>
          )}
          {visibilityComponents.gallery && (
            <SectionWrapper sx={{ backgroundColor: whiteColor, py: 5 }} id="gallery">
              <Grid container>
                <Grid item xs={12} lg={7}>
                  <Gallery images={practice.images?.images} />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          {visibilityComponents.media && (
            <section id="media" ref={ratingBarScrollEndMediaCenter}>
              <MediaCenter
                isBasic={practice.basic}
                profileAnalytics={profileAnalytics}
                profileName={practice.name[language]}
                profileImage={practice.images?.logo}
                isUnclaimed={isUnclaimedEntry}
                posts={practice.media}
                setVideoPlayerVisibility={setVideoPlayerVisibility}
              />
            </section>
          )}
          {visibilityComponents.faq && (
            <SectionWrapper withPadding sx={{ backgroundColor: whiteColor }} id="faq">
              <Grid container>
                <Grid item xs={12} md={12} lg={7}>
                  <FAQ practice={practice} onGetInTouch={toggleContactForm} hideGetInTouch={hideGetInTouch} />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          {Boolean(breadcrumbs.length) && breadcrumbs.length >= 1 && !isWPA && (
            <SectionWrapper withPadding sx={{ backgroundColor: 'background.light' }} id="breadcrumbs">
              <Grid container>
                <Grid item xs={12} md={12} lg={7}>
                  <Breadcrumbs items={mappedBreadcrumbs} />
                </Grid>
              </Grid>
            </SectionWrapper>
          )}
          {showContactForm && <BookAppointmentModal isOpen={true} onClose={toggleContactForm} practice={practice} />}
        </SearchReviewsProvider>
      </ProfileContextProvider>
      {profileVideo && videoPlayerVisibility && (
        <VideoPlayerModal
          practiceId={practice.id}
          mediaId={profileVideo?.id}
          url={profileVideo?.link}
          open={true}
          onClose={() => setVideoPlayerVisibility(false)}
        />
      )}
    </>
  )
}

// todo: fix complexity
// eslint-disable-next-line complexity
export const getStaticProps = withStaticPropsMocks(async (context: GetStaticPropsContext) => {
  const lang = context.locale as string
  const slug = context.params?.slug as string
  const language = getLanguageFromLocale(lang)
  const slugToRedirect = checkProfileSlugRedirect(slug)
  const { alternativeSearchEnabled } = getFeatureFlags()

  if (slugToRedirect) {
    return {
      redirect: {
        destination: `/${lang}/practice/${slugToRedirect}`,
        statusCode: 301
      }
    }
  }

  const practiceResult = await getPracticeBySlug({
    lang,
    slug
  })

  if (practiceResult.statusCode || practiceResult.message) {
    return returnNextErrorPage({ statusCode: 404, avoidNextBehaviour: lang === TENANTS.WPA })
  }

  const { result: practice } = practiceResult

  const topLevelKeyword =
    practice.keywords.find((keyword) => keyword?.parentName?.[language] === 'ABSTRACT_TOP_LEVEL_KEYWORD') || null

  let topStat: Statistic | null = null

  practice.statistic.forEach((obj) => {
    if (!topStat || obj.tagCount > topStat.tagCount) {
      topStat = obj
    }
  })

  const promises: Promise<any>[] = [
    getPracticeSpecialists({
      slug,
      lang,
      limit: limitSpecialists
    })
  ]

  if (!practice.basic) {
    promises.push(
      ...[
        getPracticeReviews({
          id: practice.id,
          tenant: lang,
          limit: limitReviews,
          language
        }),
        getPracticeLatestReview({
          id: practice.id,
          tenant: lang,
          language
        })
      ]
    )
  }

  const filteredPracticeData = filteringProfileDataByPlan({ practice }) as PracticeProfile
  const [practiceSpecialists, reviewResults, latestReview] = await Promise.all(promises)

  const practiceSpecialistsTransformed = {
    ...practiceSpecialists,
    result: practiceSpecialists?.result.map((practiceSpecialist: PracticeSpecialist) => {
      const tempObj = { ...practiceSpecialist }
      delete tempObj.authenticationToken
      delete tempObj.reviewCode

      return tempObj
    })
  }
  const practiceFromSpecialists = practiceSpecialistsTransformed.result
    .map((item: PracticeSpecialist) => item.keywords)
    .flat()

  const topKeyword: ProfileKeyword | null =
    topLevelKeyword ||
    practice.keywords.find((k) => k.keywordId === topStat?.tagId) ||
    practiceFromSpecialists.find((k: ProfileKeyword) => k.keywordId === topStat?.tagId) ||
    null

  const LocationId = practice.address?.LocationId
  let breadcrumbs: Breadcrumb[] = []

  if (typeof LocationId === 'number') {
    const breadcrumbsResponse = await getBreadcrumbByLocationId({ lang, id: LocationId })

    if (!('statusCode' in breadcrumbsResponse)) {
      const breadcrumbsProps =
        alternativeSearchEnabled &&
        lang !== TENANTS.WPA &&
        practice.type &&
        newSearchTypesPractices.includes(practice.type as SearchTypes)
          ? {
              objForTransform: breadcrumbsResponse,
              locale: lang,
              language,
              topKeyword: null,
              profileType: transformSearchType(practice.type)
            }
          : {
              objForTransform: breadcrumbsResponse,
              locale: lang,
              language,
              topKeyword,
              profileType: ProfileTypes.practices
            }

      breadcrumbs = breadcrumbsObjectToArray(breadcrumbsProps) || []
    }
  }

  const reviewTotalCount = reviewResults?.totalCount || 0
  const reviews = reviewResults?.result || []
  const keywordNames = reviewResults?.keywordNames || []

  return {
    props: {
      practice: {
        ...filteredPracticeData,
        specialists: practiceSpecialistsTransformed?.result?.length
          ? practiceSpecialistsTransformed.result.slice(0, 1)
          : [],
        keywords: [...practice.keywords].sort((a, b) => b.sortOrder - a.sortOrder),
        statistic: practice.statistic.reduce(mergeStatisticByName(language), [])
      },
      practiceSpecialists:
        practiceSpecialistsTransformed?.result.map((practiceSpecialist: PracticeSpecialist) =>
          filteringProfileDataByPlan({ practiceSpecialist })
        ) || [],
      totalSpecialists: practiceSpecialistsTransformed?.totalCount,
      breadcrumbs,
      latestReviews: Array.isArray(reviews) ? reviews : [],
      totalReviews: reviewTotalCount,
      latestReview: latestReview || null,
      percentage: reviewResults?.percentage || null,
      topKeyword,
      keywordNames,
      isProOrExpertPro:
        practice.plan?.includes(PracticePlans.STARTER) ||
        practice.plan?.includes(PracticePlans.ESSENTIALS) ||
        practice.plan?.includes(PracticePlans.PRO),
      isEntry: practice.plan?.includes(PracticePlans.ENTRY),
      isUnclaimedEntry:
        practice.plan?.includes(PracticePlans.UNCLAIMED) || practice.plan?.includes(PracticePlans.NO_PLAN),
      isGetInTouchDisabledPlan: practice.plan?.includes(PracticePlans.NO_PLAN),
      isStarterPlan: practice.plan?.includes(PracticePlans.STARTER)
    },
    revalidate: 60 * 60 * 8 // 8h
  }
}, 'practice/:slug')

export function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

const PracticePage = withError(PracticeMainPage)

export default PracticePage
